import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@menlo/ui/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-4 py-1.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary: 'border-transparent bg-neutral-200 text-neutral-700 [a&]:hover:bg-neutral-200/90',
        destructive:
          'border-transparent bg-red-200 text-red-950 [a&]:hover:bg-red-200/90 focus-visible:ring-red-200/20',
        warning:
          'border-transparent bg-yellow-200 text-yellow-950 [a&]:hover:bg-yellow-200/90 focus-visible:ring-yellow-200/20',
        info: 'border-transparent bg-blue-200 text-blue-950 [a&]:hover:bg-blue-200/90 focus-visible:ring-blue-200/20',
        success:
          'border-transparent bg-green-200 text-green-950 [a&]:hover:bg-green-200/90 focus-visible:ring-green-200/20',
        teal: 'border-transparent bg-teal-200 text-teal-950 [a&]:hover:bg-teal-200/90 focus-visible:ring-teal-200/20',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        dark: 'border-transparent bg-neutral-700 text-white',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
